import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  HelpCircle, Search, Plus, Pencil, Trash2, ChevronRight, Loader2, BookOpen,
  Rocket, GitBranch, Lightbulb, MessageCircleQuestion, Wrench, Eye, EyeOff, ArrowRight,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { smartSearch, normalize } from "../lib/search";
import {
  SLUG_META, CATEGORY_ORDER, FIRST_STEPS, WORKFLOWS, CONCEPTS, FAQS, TROUBLESHOOT, detectProfile,
} from "../lib/helpData";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

// ------------------------- Markdown mínimo -------------------------
const inline = (t) => {
  const parts = String(t).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="font-bold text-foreground">{p.slice(2, -2)}</strong>
    : <span key={i}>{p}</span>);
};
// Devuelve { nodes, headings:[{id,text}] } para armar el índice.
function renderMarkdown(text) {
  const lines = (text || "").split("\n");
  const out = []; const headings = []; let list = []; let ol = []; let hn = 0;
  const flush = () => {
    if (list.length) { out.push(<ul key={`u${out.length}`} className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">{list.map((l, i) => <li key={i}>{inline(l)}</li>)}</ul>); list = []; }
    if (ol.length) { out.push(<ol key={`o${out.length}`} className="list-decimal pl-5 space-y-1.5 my-2 text-muted-foreground">{ol.map((l, i) => <li key={i}>{inline(l)}</li>)}</ol>); ol = []; }
  };
  lines.forEach((raw) => {
    const l = raw.trimEnd();
    if (l.startsWith("## ")) {
      flush(); const id = `hsec-${hn++}`; const txt = l.slice(3);
      headings.push({ id, text: txt });
      out.push(<h3 key={`h${out.length}`} id={id} className="font-heading font-bold text-base mt-5 mb-1.5 text-foreground scroll-mt-4">{txt}</h3>);
    } else if (/^-\s+/.test(l)) { if (ol.length) flush(); list.push(l.replace(/^-\s+/, "")); }
    else if (/^\d+\.\s+/.test(l)) { if (list.length) flush(); ol.push(l.replace(/^\d+\.\s+/, "")); }
    else if (l.trim() === "") { flush(); }
    else { flush(); out.push(<p key={`p${out.length}`} className="text-sm text-muted-foreground leading-relaxed my-1.5">{inline(l)}</p>); }
  });
  flush();
  return { nodes: out, headings };
}

const emptyForm = { category: "", title: "", body: "", order: 100 };

// Ítems especiales (guías generales) — siempre visibles.
const SPECIAL = [
  { id: "__first", title: "Primeros pasos", icon: Rocket, cat: "Guías generales" },
  { id: "__flows", title: "Flujos de trabajo", icon: GitBranch, cat: "Guías generales" },
  { id: "__concepts", title: "Conceptos clave", icon: Lightbulb, cat: "Guías generales" },
  { id: "__faq", title: "Preguntas frecuentes", icon: MessageCircleQuestion, cat: "Guías generales" },
  { id: "__trouble", title: "Solución de problemas", icon: Wrench, cat: "Guías generales" },
];

export default function Help() {
  const { user, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({ type: "special", id: "__first" });
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/help/articles").then((r) => {
      setArticles(r.data.articles || []); setCanEdit(!!r.data.can_edit);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ¿El usuario tiene acceso al módulo del artículo?
  const accessible = (a) => {
    const meta = SLUG_META[a.slug];
    if (!meta) return true;            // artículo personalizado (admin) → visible
    if (meta.perm === null || meta.perm === undefined) return true;
    return can(meta.perm);
  };
  const visibleArticles = useMemo(
    () => (showAll ? articles : articles.filter(accessible)),
    [articles, showAll] // eslint-disable-line
  );

  // Deep-link ?m=slug (desde el botón contextual)
  useEffect(() => {
    const m = params.get("m");
    if (m && articles.length) {
      const a = articles.find((x) => x.slug === m) || articles.find((x) => x.id === m);
      if (a) { if (!accessible(a) && user?.is_admin) setShowAll(true); setSel({ type: "article", id: a.id }); }
    }
    // eslint-disable-next-line
  }, [params, articles]);

  // Índice de búsqueda unificado
  const searchIndex = useMemo(() => {
    const idx = [];
    SPECIAL.forEach((s) => idx.push({ ref: { type: "special", id: s.id }, cat: s.cat, title: s.title, text: s.title, kw: "" }));
    FIRST_STEPS.profiles.forEach((p) => idx.push({ ref: { type: "special", id: "__first" }, cat: "Guías generales", title: p.title, text: p.steps.join(" "), kw: "primeros pasos" }));
    WORKFLOWS.forEach((w) => idx.push({ ref: { type: "special", id: "__flows" }, cat: "Flujos de trabajo", title: w.title, text: w.intro + " " + w.steps.map((s) => s.module + " " + s.desc).join(" "), kw: "flujo proceso" }));
    CONCEPTS.forEach((c) => idx.push({ ref: { type: "special", id: "__concepts" }, cat: "Conceptos clave", title: c.title, text: c.body, kw: "concepto" }));
    FAQS.forEach((f, i) => idx.push({ ref: { type: "special", id: "__faq" }, cat: "Preguntas frecuentes", title: f.q, text: f.a, kw: "faq pregunta", faqIndex: i }));
    TROUBLESHOOT.forEach((t) => idx.push({ ref: { type: "special", id: "__trouble" }, cat: "Solución de problemas", title: t.problem, text: t.causes.join(" "), kw: "problema error no funciona" }));
    visibleArticles.forEach((a) => idx.push({ ref: { type: "article", id: a.id }, cat: a.category, title: a.title, text: a.body, kw: (SLUG_META[a.slug]?.keywords || []).join(" ") }));
    return idx;
  }, [visibleArticles]);

  const results = useMemo(() => {
    if (!q.trim()) return null;
    return smartSearch(searchIndex, q, (it) => [it.title, it.kw, it.text]);
  }, [q, searchIndex]);

  // Agrupado por categoría para el índice lateral
  const grouped = useMemo(() => {
    const g = {};
    visibleArticles.forEach((a) => { (g[a.category] = g[a.category] || []).push(a); });
    Object.values(g).forEach((arr) => arr.sort((x, y) => (x.order || 100) - (y.order || 100)));
    const cats = Object.keys(g).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a), ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return { g, cats };
  }, [visibleArticles]);

  const activeArticle = sel.type === "article" ? articles.find((a) => a.id === sel.id) : null;
  const activeSpecial = sel.type === "special" ? SPECIAL.find((s) => s.id === sel.id) : null;
  const profileId = detectProfile(user);

  // ------------------------- editor (admin) -------------------------
  const openNew = () => { setEditing(null); setForm(emptyForm); setEditOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ category: a.category, title: a.title, body: a.body, order: a.order }); setEditOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/help/articles/${editing.id}`, form);
      else await api.post("/help/articles", form);
      toast.success("Guardado"); setEditOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.title}"?`)) return;
    try { await api.delete(`/help/articles/${a.id}`); toast.success("Eliminado"); load(); setSel({ type: "special", id: "__first" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pick = (ref) => { setQ(""); setSel(ref); if (params.get("m")) { params.delete("m"); setParams(params, { replace: true }); } };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-9rem)]" data-testid="help-page">
      {/* Índice lateral */}
      <div className="lg:w-80 shrink-0 flex flex-col border border-border rounded-sm bg-card overflow-hidden">
        <div className="p-3 border-b border-border space-y-2">
          <h2 className="font-heading font-bold flex items-center gap-2"><HelpCircle className="h-5 w-5 text-primary" />Centro de Ayuda</h2>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar: crear cotización, pagar…" className="rounded-sm pl-8 h-9" data-testid="help-search" />
          </div>
          {user?.is_admin && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 rounded-sm text-xs" onClick={() => setShowAll((v) => !v)} data-testid="help-toggle-all">
                {showAll ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                {showAll ? "Solo mi perfil" : "Ver toda la doc."}
              </Button>
              {canEdit && <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={openNew} data-testid="new-article-btn"><Plus className="h-3.5 w-3.5" /></Button>}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2" data-testid="help-index">
          {loading && <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}

          {/* Resultados de búsqueda */}
          {!loading && results && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground px-2 py-1">Resultados ({results.length})</p>
              {results.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">Sin resultados.</p>}
              {results.map((r, i) => (
                <button key={i} onClick={() => pick(r.ref)} data-testid="help-result"
                  className="w-full flex flex-col gap-0.5 px-2 py-2 rounded-sm text-left hover:bg-muted transition-colors">
                  <span className="text-sm font-medium truncate flex items-center gap-1.5"><ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary" />{r.title}</span>
                  <span className="text-[11px] text-muted-foreground pl-5">{r.cat}</span>
                </button>
              ))}
            </div>
          )}

          {/* Navegación estructurada */}
          {!loading && !results && (
            <>
              <div className="mb-2">
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground px-2 py-1">Guías generales</p>
                {SPECIAL.map((s) => (
                  <button key={s.id} onClick={() => pick({ type: "special", id: s.id })} data-testid={`help-nav-${s.id.replace(/_/g, "")}`}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-left text-sm hover:bg-muted transition-colors ${sel.type === "special" && sel.id === s.id ? "bg-muted font-semibold" : ""}`}>
                    <s.icon className={`h-4 w-4 shrink-0 ${sel.type === "special" && sel.id === s.id ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="truncate">{s.title}</span>
                  </button>
                ))}
              </div>
              {grouped.cats.map((cat) => (
                <div key={cat} className="mb-2">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground px-2 py-1">{cat}</p>
                  {grouped.g[cat].map((a) => (
                    <button key={a.id} onClick={() => pick({ type: "article", id: a.id })} data-testid={`article-${a.slug || a.id}`}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-left text-sm hover:bg-muted transition-colors ${activeArticle?.id === a.id ? "bg-muted font-semibold" : ""}`}>
                      <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${activeArticle?.id === a.id ? "text-primary" : "text-muted-foreground"}`} />
                      <span className="truncate">{a.title}</span>
                    </button>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 border border-border rounded-sm bg-card overflow-y-auto" data-testid="help-content">
        {loading && <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {!loading && (
          <div className="p-6 max-w-3xl">
            {/* Breadcrumbs */}
            <Breadcrumb className="mb-3">
              <BreadcrumbList>
                <BreadcrumbItem><BreadcrumbLink asChild><button onClick={() => pick({ type: "special", id: "__first" })} className="text-xs">Ayuda</button></BreadcrumbLink></BreadcrumbItem>
                <BreadcrumbSeparator />
                {activeArticle && <><BreadcrumbItem><span className="text-xs">{activeArticle.category}</span></BreadcrumbItem><BreadcrumbSeparator /><BreadcrumbItem><BreadcrumbPage className="text-xs">{activeArticle.title}</BreadcrumbPage></BreadcrumbItem></>}
                {activeSpecial && <><BreadcrumbItem><span className="text-xs">Guías generales</span></BreadcrumbItem><BreadcrumbSeparator /><BreadcrumbItem><BreadcrumbPage className="text-xs">{activeSpecial.title}</BreadcrumbPage></BreadcrumbItem></>}
              </BreadcrumbList>
            </Breadcrumb>

            {activeArticle && <ArticleView article={activeArticle} canEdit={canEdit} onEdit={openEdit} onDelete={del} onOpenSlug={(slug) => { const a = articles.find((x) => x.slug === slug); if (a) pick({ type: "article", id: a.id }); }} />}
            {sel.type === "special" && sel.id === "__first" && <FirstSteps profileId={profileId} isAdmin={!!user?.is_admin} />}
            {sel.type === "special" && sel.id === "__flows" && <Flows can={can} onOpenSlug={(slug) => { const a = articles.find((x) => x.slug === slug); if (a) pick({ type: "article", id: a.id }); }} />}
            {sel.type === "special" && sel.id === "__concepts" && <Concepts />}
            {sel.type === "special" && sel.id === "__faq" && <Faqs onOpenSlug={(slug) => { const a = articles.find((x) => x.slug === slug); if (a) pick({ type: "article", id: a.id }); }} />}
            {sel.type === "special" && sel.id === "__trouble" && <Troubles onOpenSlug={(slug) => { const a = articles.find((x) => x.slug === slug); if (a) pick({ type: "article", id: a.id }); }} />}
          </div>
        )}
      </div>

      {/* Editor admin */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar artículo" : "Nuevo artículo"}</DialogTitle>
            <DialogDescription>Usa <code>## Título</code> para encabezados, <code>-</code> para viñetas, <code>1.</code> para pasos y <code>**texto**</code> para negrita.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej. Comercial" className="rounded-sm" data-testid="article-category" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Orden</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="rounded-sm" data-testid="article-order" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="article-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Contenido</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} className="rounded-sm font-mono text-xs" data-testid="article-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} disabled={!form.title.trim() || !form.category.trim()} data-testid="save-article-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Sub-vistas -------------------------
function ArticleView({ article, canEdit, onEdit, onDelete, onOpenSlug }) {
  const { nodes, headings } = useMemo(() => renderMarkdown(article.body), [article]);
  const meta = SLUG_META[article.slug];
  const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-primary">{article.category}</p>
          <h1 className="font-heading font-black text-2xl">{article.title}</h1>
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(article)} data-testid="edit-article-btn"><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onDelete(article)} data-testid="delete-article-btn"><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      {meta?.route && meta.route !== "/ayuda" && (
        <a href={meta.route} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline mb-3" data-testid="help-goto-module">
          Ir al módulo <ArrowRight className="h-3.5 w-3.5" />
        </a>
      )}

      {headings.length > 2 && (
        <div className="mb-4 p-3 rounded-sm bg-muted/50 border border-border">
          <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">En este artículo</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {headings.map((h) => (
              <button key={h.id} onClick={() => scrollTo(h.id)} className="text-xs text-primary hover:underline text-left">{h.text}</button>
            ))}
          </div>
        </div>
      )}
      <div>{nodes}</div>
    </div>
  );
}

function FirstSteps({ profileId, isAdmin }) {
  const gp = FIRST_STEPS.general;
  const profiles = FIRST_STEPS.profiles.filter((p) => !p.adminOnly || isAdmin);
  const mine = profiles.find((p) => p.id === profileId);
  const ordered = mine ? [mine, ...profiles.filter((p) => p.id !== profileId)] : profiles;
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Guías generales</p>
      <h1 className="font-heading font-black text-2xl mb-1">Primeros pasos</h1>
      <p className="text-sm text-muted-foreground mb-4">{gp.intro}</p>
      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground mb-6">{gp.steps.map((s, i) => <li key={i}>{inline(s)}</li>)}</ol>

      <h2 className="font-heading font-bold text-lg mb-2">Según tu perfil</h2>
      <Accordion type="single" collapsible defaultValue={mine?.id} className="border-t border-border">
        {ordered.map((p) => (
          <AccordionItem key={p.id} value={p.id}>
            <AccordionTrigger className="font-semibold">
              {p.title}{p.id === profileId && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm">Tu perfil</span>}
            </AccordionTrigger>
            <AccordionContent><ol className="list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground">{p.steps.map((s, i) => <li key={i}>{inline(s)}</li>)}</ol></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function Flows({ can, onOpenSlug }) {
  const flows = WORKFLOWS.filter((w) => !w.perm || can(w.perm));
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Guías generales</p>
      <h1 className="font-heading font-black text-2xl mb-1">Flujos de trabajo</h1>
      <p className="text-sm text-muted-foreground mb-4">Procesos completos que atraviesan varios módulos. Al guardar en un paso, la información pasa automáticamente al siguiente.</p>
      <Accordion type="single" collapsible defaultValue={flows[0]?.id} className="border-t border-border">
        {flows.map((w) => (
          <AccordionItem key={w.id} value={w.id}>
            <AccordionTrigger className="font-semibold">{w.title}</AccordionTrigger>
            <AccordionContent>
              <p className="text-sm text-muted-foreground mb-3">{w.intro}</p>
              <ol className="space-y-2.5">
                {w.steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <div className="text-sm">
                      <button onClick={() => onOpenSlug(s.slug)} className="font-semibold text-foreground hover:text-primary hover:underline">{s.module}</button>
                      <span className="text-muted-foreground"> — {s.desc}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function Concepts() {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Guías generales</p>
      <h1 className="font-heading font-black text-2xl mb-1">Conceptos clave</h1>
      <p className="text-sm text-muted-foreground mb-4">Ideas transversales que conviene entender para usar bien el sistema.</p>
      <Accordion type="single" collapsible className="border-t border-border">
        {CONCEPTS.map((c) => (
          <AccordionItem key={c.id} value={c.id}>
            <AccordionTrigger className="font-semibold">{c.title}</AccordionTrigger>
            <AccordionContent><p className="text-sm text-muted-foreground leading-relaxed">{inline(c.body)}</p></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function Faqs({ onOpenSlug }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Guías generales</p>
      <h1 className="font-heading font-black text-2xl mb-1">Preguntas frecuentes</h1>
      <p className="text-sm text-muted-foreground mb-4">Respuestas rápidas a las dudas más comunes.</p>
      <Accordion type="single" collapsible className="border-t border-border">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`}>
            <AccordionTrigger className="font-semibold">{f.q}</AccordionTrigger>
            <AccordionContent>
              <p className="text-sm text-muted-foreground leading-relaxed">{inline(f.a)}</p>
              {f.slug && <button onClick={() => onOpenSlug(f.slug)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Ver guía del módulo <ArrowRight className="h-3.5 w-3.5" /></button>}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function Troubles({ onOpenSlug }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Guías generales</p>
      <h1 className="font-heading font-black text-2xl mb-1">Solución de problemas</h1>
      <p className="text-sm text-muted-foreground mb-4">Si algo no funciona como esperabas, revisa primero estas causas normales. Si el problema continúa, comunícate con el administrador.</p>
      <Accordion type="single" collapsible className="border-t border-border">
        {TROUBLESHOOT.map((t, i) => (
          <AccordionItem key={i} value={`t-${i}`}>
            <AccordionTrigger className="font-semibold">{t.problem}</AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">{t.causes.map((c, j) => <li key={j}>{inline(c)}</li>)}</ul>
              {t.slug && <button onClick={() => onOpenSlug(t.slug)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Ver guía del módulo <ArrowRight className="h-3.5 w-3.5" /></button>}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
